import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calculator, ArrowRight, TrendingUp, PiggyBank, BarChart3, ChevronDown, HelpCircle, Check, Shield, Clock, Award } from "lucide-react";
import { Link } from "react-router-dom";
import { simulerRentabilite } from "@/lib/api";

import { IMG_SIMULATEUR } from "../../data/images";
const HERO_IMAGE = IMG_SIMULATEUR;

const comparatifData = [
  { label: "Type de bail", classique: "Bail 3-6 ans", courteDuree: "Bail civil / Mandat" },
  { label: "Loyer mensuel (T2 Limoges)", classique: "~450 EUR", courteDuree: "~750-1000 EUR*" },
  { label: "Vacance locative", classique: "1-2 mois/an", courteDuree: "Minimisée" },
  { label: "Impayés", classique: "Risque réel", courteDuree: "Aucun (loyer garanti)" },
  { label: "Entretien", classique: "À votre charge", courteDuree: "Inclus" },
  { label: "Flexibilité", classique: "Très faible", courteDuree: "Élevée" },
];

const methodologySteps = [
  "Analyse des données du marché local (Airbnb, Booking, Abritel)",
  "Prise en compte de la saisonnalité et des événements locaux",
  "Étude des biens similaires dans votre quartier",
  "Calcul du taux d'occupation prévisionnel",
  "Application de nos données historiques réelles",
];

const miniFaqs = [
  { q: "L'estimation est-elle fiable ?", a: "Notre algorithme s'appuie sur les données réelles du marché local et notre historique de gestion. L'estimation est une moyenne prudente qui reflète ce que vous pouvez raisonnablement attendre." },
  { q: "Que comprennent les revenus affichés ?", a: "Les revenus affichés sont des revenus bruts estimés avant commission de gestion. Nous déduirons notre commission uniquement si vous choisissez l'offre Partenariat." },
  { q: "Comment maximiser mes revenus ?", a: "Plusieurs leviers : qualité de l'ameublement, photos professionnelles, description optimisée, tarification dynamique. Nous gérons tout cela pour vous." },
];

export default function SimulateurPage() {
  const [simulateur, setSimulateur] = useState({
    prix_achat: 150000,
    type_bien: "appartement",
    surface: 45,
    capacite: 4,
    charges_mensuelles: 150,
  });
  const [simulation, setSimulation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleSimulation = async () => {
    setLoading(true);
    try {
      const res = await simulerRentabilite(simulateur);
      setSimulation(res.data);
    } catch (err) {
      console.error("Erreur simulation:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="simulateur-page">
      {/* Hero */}
      <section className="relative min-h-[40vh] flex items-end overflow-hidden">
        <img src={HERO_IMAGE} alt="Simulateur de rentabilité" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1008]/90 via-[#1a1008]/40 to-[#1a1008]/10" />
        <div className="relative w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 pt-32">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3 mb-4">
            <Calculator className="w-6 h-6 text-[#C9922A]" />
            <span className="text-[#C9922A] uppercase tracking-[0.2em] text-sm font-semibold">Simulateur</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
            Calculez votre rentabilité
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-white/60 mt-4 max-w-2xl text-lg">
            Estimez les revenus potentiels de votre bien en location courte durée à Limoges et ses environs.
          </motion.p>
        </div>
      </section>

      {/* Simulateur */}
      <section className="py-20 bg-[#F5F0EB]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Form */}
            <div>
              <h2 className="font-display text-2xl text-[#004333] mb-6">Votre bien</h2>
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#2D2418] mb-2">Prix d'achat du bien</label>
                  <div className="relative">
                    <input type="number" value={simulateur.prix_achat} onChange={(e) => setSimulateur({ ...simulateur, prix_achat: parseFloat(e.target.value) })}
                      className="w-full px-4 py-3 rounded-xl border border-[#E0D6CA] bg-white text-[#2D2418] focus:border-[#C9922A] focus:ring-1 focus:ring-[#C9922A]/30 outline-none" data-testid="sim-prix" />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6B5E50] text-sm">EUR</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#2D2418] mb-2">Type de bien</label>
                    <select value={simulateur.type_bien} onChange={(e) => setSimulateur({ ...simulateur, type_bien: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-[#E0D6CA] bg-white text-[#2D2418] focus:border-[#C9922A] focus:ring-1 focus:ring-[#C9922A]/30 outline-none" data-testid="sim-type">
                      <option value="studio">Studio</option>
                      <option value="appartement">Appartement</option>
                      <option value="maison">Maison</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2D2418] mb-2">Surface (m2)</label>
                    <input type="number" value={simulateur.surface} onChange={(e) => setSimulateur({ ...simulateur, surface: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 rounded-xl border border-[#E0D6CA] bg-white text-[#2D2418] focus:border-[#C9922A] focus:ring-1 focus:ring-[#C9922A]/30 outline-none" data-testid="sim-surface" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#2D2418] mb-2">Capacité (pers.)</label>
                    <input type="number" value={simulateur.capacite} onChange={(e) => setSimulateur({ ...simulateur, capacite: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 rounded-xl border border-[#E0D6CA] bg-white text-[#2D2418] focus:border-[#C9922A] focus:ring-1 focus:ring-[#C9922A]/30 outline-none" data-testid="sim-capacite" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2D2418] mb-2">Charges mensuelles</label>
                    <div className="relative">
                      <input type="number" value={simulateur.charges_mensuelles} onChange={(e) => setSimulateur({ ...simulateur, charges_mensuelles: parseFloat(e.target.value) })}
                        className="w-full px-4 py-3 rounded-xl border border-[#E0D6CA] bg-white text-[#2D2418] focus:border-[#C9922A] focus:ring-1 focus:ring-[#C9922A]/30 outline-none" data-testid="sim-charges" />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6B5E50] text-sm">EUR</span>
                    </div>
                  </div>
                </div>
                <button onClick={handleSimulation} disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-[#004333] text-white rounded-xl font-semibold hover:bg-[#003328] transition-colors disabled:opacity-50" data-testid="sim-submit">
                  {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><Calculator className="w-5 h-5" /> Calculer ma rentabilité</>}
                </button>
              </div>
            </div>

            {/* Results */}
            <div>
              {simulation ? (
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl border border-[#E0D6CA] shadow-sm p-8" data-testid="sim-results">
                  <h3 className="font-display text-2xl text-[#004333] mb-6">Résultats estimés</h3>
                  <div className="space-y-4 mb-8">
                    {[
                      { label: "Prix par nuit estimé", value: `${simulation.prix_nuit_estime} EUR` },
                      { label: "Taux d'occupation", value: simulation.taux_occupation },
                      { label: "Revenu brut mensuel", value: `${simulation.revenu_brut_mensuel} EUR`, icon: TrendingUp },
                      { label: "Revenu net mensuel", value: `${simulation.revenu_net_mensuel} EUR`, icon: PiggyBank, highlight: true },
                      { label: "Revenu net annuel", value: `${simulation.revenu_net_annuel} EUR`, icon: BarChart3 },
                    ].map((item, i) => (
                      <div key={i} className="flex justify-between items-center py-3 border-b border-[#E0D6CA]/50">
                        <span className="text-[#6B5E50] text-sm">{item.label}</span>
                        <span className={`font-mono font-bold ${item.highlight ? "text-xl text-[#C9922A]" : "text-[#004333]"}`}>{item.value}</span>
                      </div>
                    ))}
                    {simulation.rendement_brut && (
                      <div className="flex justify-between items-center py-3">
                        <span className="text-[#6B5E50] text-sm">Rendement brut</span>
                        <span className="font-mono font-bold text-[#004333]">{simulation.rendement_brut}</span>
                      </div>
                    )}
                  </div>
                  <Link to="/contact" className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#C9922A] text-white rounded-xl font-semibold hover:bg-[#B8831F] transition-colors" data-testid="sim-cta">
                    Confier mon bien <ArrowRight className="w-4 h-4" />
                  </Link>
                </motion.div>
              ) : (
                <div className="bg-white/50 rounded-2xl border border-[#E0D6CA]/50 p-8 flex flex-col items-center justify-center min-h-[400px] text-center">
                  <div className="w-16 h-16 rounded-2xl bg-[#C9922A]/10 flex items-center justify-center mb-5">
                    <BarChart3 className="w-8 h-8 text-[#C9922A]" />
                  </div>
                  <h3 className="font-display text-xl text-[#004333] mb-2">Vos résultats apparaîtront ici</h3>
                  <p className="text-[#6B5E50] text-sm max-w-xs">Remplissez le formulaire et cliquez sur "Calculer" pour obtenir une estimation personnalisée.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Notre méthodologie */}
      <section className="py-20 bg-[#EDE8E1]" data-testid="sim-methodology">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#C9922A] uppercase tracking-[0.2em] text-sm font-semibold mb-3 block">Méthodologie</span>
              <h2 className="font-display text-3xl md:text-4xl text-[#004333] mb-4">Comment calculons-nous vos revenus ?</h2>
              <p className="text-[#6B5E50] leading-relaxed mb-6">
                Notre algorithme de simulation s'appuie sur les données réelles du marché locatif courte durée à Limoges et Évaux-les-Bains, combinées à notre propre historique de gestion.
              </p>
              <ul className="space-y-3">
                {methodologySteps.map((step, i) => (
                  <motion.li key={i} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#004333] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[10px] font-bold text-white">{i + 1}</span>
                    </div>
                    <span className="text-[#6B5E50] text-sm">{step}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-[#E0D6CA] shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#C9922A]/10 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-[#C9922A]" />
                </div>
                <h3 className="font-semibold text-[#004333]">Pourquoi nous faire confiance ?</h3>
              </div>
              <div className="space-y-4">
                {[
                  { icon: Award, text: "Estimations basées sur nos résultats réels" },
                  { icon: Clock, text: "Données mises à jour chaque trimestre" },
                  { icon: BarChart3, text: "Projections prudentes et réalistes" },
                  { icon: Check, text: "Validation par notre équipe d'experts locaux" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <item.icon className="w-4 h-4 text-[#C9922A]" />
                    <span className="text-[#6B5E50] text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparatif Location classique vs Courte durée */}
      <section className="py-20 bg-[#1A120B]" data-testid="sim-comparatif">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#C9922A] uppercase tracking-[0.2em] text-sm font-semibold mb-3 block">Comparatif</span>
            <h2 className="font-display text-3xl md:text-4xl text-white mb-3">Location classique vs. Courte durée</h2>
            <p className="text-white/40 max-w-lg mx-auto text-sm">Découvrez pourquoi de plus en plus de propriétaires choisissent la location courte durée gérée par un professionnel.</p>
          </div>
          <div className="bg-white/[0.04] rounded-2xl border border-white/10 overflow-hidden">
            <div className="grid grid-cols-3 bg-white/[0.06] px-6 py-4 border-b border-white/10">
              <span className="text-white/40 text-xs uppercase tracking-wider font-semibold" />
              <span className="text-white/40 text-xs uppercase tracking-wider font-semibold text-center">Location classique</span>
              <span className="text-[#C9922A] text-xs uppercase tracking-wider font-semibold text-center">Courte durée gérée</span>
            </div>
            {comparatifData.map((row, i) => (
              <motion.div key={i} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }} className="grid grid-cols-3 px-6 py-4 border-b border-white/[0.06] last:border-0">
                <span className="text-white/70 text-sm">{row.label}</span>
                <span className="text-white/30 text-sm text-center">{row.classique}</span>
                <span className="text-[#C9922A]/80 text-sm text-center font-medium">{row.courteDuree}</span>
              </motion.div>
            ))}
          </div>
          <p className="text-white/20 text-xs text-center mt-4">*Estimation moyenne après frais de gestion, basée sur un T2 meublé à Limoges centre.</p>
        </div>
      </section>

      {/* Mini FAQ */}
      <section className="py-20 bg-[#F5F0EB]" data-testid="sim-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <HelpCircle className="w-8 h-8 text-[#C9922A] mx-auto mb-3" />
            <h2 className="font-display text-3xl md:text-4xl text-[#004333]">Questions fréquentes</h2>
          </div>
          <div className="space-y-3">
            {miniFaqs.map((faq, i) => (
              <div key={i} className="border border-[#E0D6CA] bg-[#FFFDF9] rounded-xl overflow-hidden shadow-sm">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left hover:bg-[#EDE8E1]/50 transition-colors" data-testid={`sim-faq-${i}`}>
                  <span className="font-semibold text-[#004333] pr-4 text-sm">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-[#C9922A] flex-shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
                      <div className="px-5 pb-5 text-[#6B5E50] text-sm leading-relaxed border-t border-[#E8E0D4] pt-4">{faq.a}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#004333]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-display text-2xl md:text-3xl text-white mb-4">Convaincu par les chiffres ?</h2>
          <p className="text-white/60 mb-8 max-w-xl mx-auto">
            Contactez-nous pour une étude approfondie et personnalisée de votre bien. C'est gratuit et sans engagement.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#C9922A] text-white rounded-full font-semibold hover:bg-[#B8831F] transition-colors">
              Demander une étude gratuite <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/sous-location" className="inline-flex items-center gap-2 px-8 py-3.5 border-2 border-white/30 text-white rounded-full font-semibold hover:bg-white/10 transition-colors">
              Découvrir l'offre Sérénité
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
